import {
  HttpDownloadProgressEvent,
  HttpEvent,
  HttpEventType,
} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FlexibleColumn, IpoLanguageService } from '@ip-only/front-end-library';
import { Subject, takeUntil } from 'rxjs';
import { ToastsService } from '../../../shared/toast/toast.service';
import { ScreenSizeClassComponent } from '../../../shared/util/screen-size/screen-size.component';
import {
  CombinedProjectOverviewResponse,
  ProjectOverview,
} from '../../delivery-overview.entities';
import { DeliveryOverviewService } from '../../service/delivery-overview.service';
import { ProjectCompletionModalService } from '../../service/project-completion-modal.service';
import { ProjectsOverviewService } from '../../service/projects-overview.service';
import { DeliveryOverviewTabs } from '../delivery-overview-wrapper.component';
import {
  deliveryOverviewTableColumnsAll,
  deliveryOverviewTableColumnsStarred,
} from './delivery-overview-table-columns';

@Component({
  selector: 'app-delivery-overview-table',
  templateUrl: './delivery-overview-table.component.html',
  styleUrls: ['./delivery-overview-table.component.scss'],
  standalone: false,
})
export class DeliveryOverviewTableComponent
  extends ScreenSizeClassComponent
  implements OnInit, OnDestroy
{
  private readonly projectCompletionModalService = inject(
    ProjectCompletionModalService,
  );
  @Input() activeTab: DeliveryOverviewTabs;
  @Input() projects: CombinedProjectOverviewResponse[];
  language$ = this.languageService.getLanguage$();

  columns: FlexibleColumn[];

  filterFields = ['value', 'name'];
  inputSearch = this.deliveryOverviewService.filterString;
  eventResponse: ProjectOverview[] = [];

  isModalOpen = this.projectCompletionModalService.isModalOpen;

  DeliveryOverviewTabs: DeliveryOverviewTabs;

  completedProject = this.projectCompletionModalService.completedProject;

  projectsAmount = signal(0);
  loadedProjectObjectsAmount = signal(0);
  hasProjectsFinishedLoading = computed(
    () => this.projectsAmount() === this.loadedProjectObjectsAmount(),
  );

  private cancelSub$ = new Subject<void>();

  constructor(
    private languageService: IpoLanguageService,
    private deliveryOverviewService: DeliveryOverviewService,
    private projectOverviewService: ProjectsOverviewService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastsService: ToastsService,
  ) {
    effect(() => {
      this.updateProject();
    });
    super();
  }

  ngOnInit(): void {
    this.setColumns();
    if (this.activeTab === DeliveryOverviewTabs.Starred) {
      this.fetchProjects();
    }
  }

  ngOnDestroy(): void {
    this.cancelSub$.next();
  }

  setColumns() {
    if (this.activeTab === DeliveryOverviewTabs.All) {
      this.columns = deliveryOverviewTableColumnsAll;
      return;
    }
    this.columns = deliveryOverviewTableColumnsStarred;
  }

  fetchProjects() {
    const projectNumbers = this.getMappedProjectValues();
    this.projectsAmount.set(projectNumbers.length);
    this.loadedProjectObjectsAmount.set(0);

    this.projectOverviewService
      .getEventStreamedProjects$(projectNumbers)
      .pipe(takeUntil(this.cancelSub$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            this.getStreamedProjectOverviewObjects(event);
          } else if (event.type === HttpEventType.Response) {
            this.changeDetectorRef.detectChanges();
          }
        },
        error: (err) => {
          console.error(err);
          this.toastsService.addToast(
            { type: 'error', message: 'error' },
            6000,
          );
        },
      });
  }

  updateProject() {
    const completedProject = this.completedProject();
    if (completedProject && this.projects) {
      this.projects = this.projects.map((project) => {
        return project.id === completedProject.id ? completedProject : project;
      });
    }
  }

  getMappedProjectValues() {
    return this.projects
      .filter(
        (project: CombinedProjectOverviewResponse) =>
          !project.spans && project.value,
      )
      .map(({ value }) => value);
  }

  getStreamedProjectOverviewObjects(streamData: HttpEvent<string>): void {
    const content: string = (streamData as HttpDownloadProgressEvent)
      .partialText as string;
    if (content) {
      this.setEventResponse(content);

      const combined = this.getCombinedOverviewResponse();

      this.projects = combined;
      this.setActiveTab(combined);
      this.loadedProjectObjectsAmount.update((amount) => ++amount);
      this.changeDetectorRef.detectChanges();
    }
  }

  setEventResponse(content: string): void {
    const array = content.split('data:');
    const filter = array.filter((string) => {
      return string.length > 0;
    });

    this.eventResponse = filter.map((stringObj) => {
      return JSON.parse(stringObj) as ProjectOverview;
    });
  }

  getCombinedOverviewResponse(): CombinedProjectOverviewResponse[] {
    return this.projects.map((project) => {
      const eventResponseProject = this.eventResponse.find(
        ({ projectID }) => projectID === project.value,
      );

      return {
        ...project,
        ...eventResponseProject,
      } as CombinedProjectOverviewResponse;
    });
  }

  setActiveTab(combined: CombinedProjectOverviewResponse[]): void {
    this.activeTab === DeliveryOverviewTabs.All
      ? this.deliveryOverviewService.setProjects(combined)
      : this.deliveryOverviewService.setStarredProjects(combined);
  }

  handleClickStarButton(project: CombinedProjectOverviewResponse) {
    this.deliveryOverviewService.onUpdateStarredProject(project);
  }

  handleClickCompleteProject(project: CombinedProjectOverviewResponse) {
    this.projectCompletionModalService.setSelectedProject(project);
    this.projectCompletionModalService.setIsModalOpen(true);
  }
}
